import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './CrystalBallFortune.css';

// Contract ABIs - MATCHED TO YOUR ACTUAL CONTRACT
const CRYSTALBALL_ABI = [
  // Game functions
  "function selectOrb(uint8 orbChoice, uint256 betAmount) external",
  "function getGameStats() external view returns (uint256 totalGames, uint256 totalBets, uint256 totalPayoutsAmount, uint256 houseProfit, uint256 companyTotal, uint256 routerTotal)",
  "function getRecentGames(uint256 count) external view returns (tuple(address player, uint256 betAmount, uint8 playerChoice, uint8 result, uint256 grossPayout, uint256 netPayout, uint256 timestamp, bool won)[])",
  "function getGameConfig() external view returns (tuple(uint256 noWinChance, uint256[5] orbChances, uint256 orbWinPayout))",
  "function getOrbInfo(uint8 orbIndex) external view returns (string memory symbol, string memory name, uint256 chance, uint256 payout)",
  "function getAllOrbChances() external view returns (uint256[5])",
  "function getDividendInfo() external view returns (address dividendContractAddress, bool isDividendEnabled)",
  "function getOrbStats() external view returns (uint256[5] memory orbWins, uint256[5] memory orbSelections, uint256 houseWins)",
  "event GamePlayed(address indexed player, uint256 betAmount, uint8 playerChoice, uint8 result, uint256 grossPayout, uint256 netPayout, bool won)",
  "event OrbsRevealed(uint8 winningOrb, string symbol)",
  
  // Admin functions - READ FUNCTIONS
  "function owner() external view returns (address)",
  "function admin() external view returns (address)",
  "function paused() external view returns (bool)",
  "function houseBalance() external view returns (uint256)",
  "function minBet() external view returns (uint256)",
  "function maxBet() external view returns (uint256)",
  
  // Admin functions - WRITE FUNCTIONS
  "function pauseGame() external",
  "function unpauseGame() external", 
  "function updateGameConfig(tuple(uint256 noWinChance, uint256[5] orbChances, uint256 orbWinPayout) newConfig) external",
  "function updateWinChances(uint256 noWin, uint256[5] orbChances) external",
  "function updateSingleOrbChance(uint8 orbIndex, uint256 newChance) external",
  "function updatePayout(uint256 newPayout) external",
  "function setBetLimits(uint256 minBetAmount, uint256 maxBetAmount) external",
  "function fundHouse(uint256 amount) external",
  "function withdrawHouse(uint256 amount) external",
  "function emergencyWithdrawAll() external",
  "function toggleDividend(bool enabled) external",
  
  // Utility functions
  "function getEffectiveMultiplier() external view returns (uint256)",
  "function calculateHouseEdge() external view returns (uint256)"
];

const BLB_TOKEN_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function transfer(address to, uint256 amount) external returns (bool)"
];

// Contract Addresses
const CONTRACT_ADDRESSES = {
  CRYSTALBALL: "0x01d052753Bd6A6999F23C4FCdCEEEB8B581f281F", // Replace with actual address
  BLB_TOKEN: "0x9015e335f28cbad84EB255d5205CDb51B08A1B70"
};

// 🔮 EPIC MYSTICAL SOUND SYSTEM 🔮
const playSound = (type, volume = 0.3) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const createTone = (frequency, duration, waveType = 'sine') => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = waveType;
      
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    };

    const createComplexSound = (notes, duration) => {
      notes.forEach((note, index) => {
        setTimeout(() => {
          createTone(note.freq, note.duration || duration, note.wave || 'sine');
        }, note.delay || index * 50);
      });
    };

    switch (type) {
      case 'crystalReveal':
        createComplexSound([
          { freq: 440, duration: 2, wave: 'sine', delay: 0 },
          { freq: 554, duration: 1.8, wave: 'triangle', delay: 200 },
          { freq: 659, duration: 1.6, wave: 'sine', delay: 400 },
          { freq: 880, duration: 1.4, wave: 'triangle', delay: 600 },
          { freq: 1108, duration: 1.2, wave: 'sine', delay: 800 }
        ], 1.5);
        break;
        
      case 'epicWin':
        createComplexSound([
          { freq: 659, duration: 0.6, wave: 'triangle', delay: 0 },
          { freq: 784, duration: 0.6, wave: 'sine', delay: 200 },
          { freq: 988, duration: 0.7, wave: 'triangle', delay: 400 },
          { freq: 1319, duration: 0.8, wave: 'sine', delay: 600 },
          { freq: 1760, duration: 1.0, wave: 'sine', delay: 800 }
        ], 0.4);
        break;
        
      case 'bigWin':
        createComplexSound([
          { freq: 523, duration: 0.4, wave: 'triangle', delay: 0 },
          { freq: 659, duration: 0.4, wave: 'sine', delay: 150 },
          { freq: 784, duration: 0.5, wave: 'triangle', delay: 300 },
          { freq: 1047, duration: 0.6, wave: 'sine', delay: 450 }
        ], 0.3);
        break;
        
      case 'smallWin':
        createComplexSound([
          { freq: 440, duration: 0.3, wave: 'triangle', delay: 0 },
          { freq: 523, duration: 0.3, wave: 'sine', delay: 100 },
          { freq: 659, duration: 0.4, wave: 'sine', delay: 200 }
        ], 0.2);
        break;
        
      case 'lose':
        createComplexSound([
          { freq: 400, duration: 0.5, wave: 'triangle', delay: 0 },
          { freq: 300, duration: 0.6, wave: 'sawtooth', delay: 200 },
          { freq: 200, duration: 0.7, wave: 'sawtooth', delay: 400 }
        ], 0.3);
        break;
        
      case 'buttonHover':
        createTone(660, 0.1, 'triangle');
        break;
        
      case 'buttonClick':
        createComplexSound([
          { freq: 880, duration: 0.05, wave: 'square', delay: 0 },
          { freq: 1100, duration: 0.05, wave: 'triangle', delay: 20 }
        ], 0.05);
        break;
        
      case 'orbSelect':
        createComplexSound([
          { freq: 550, duration: 0.2, wave: 'triangle', delay: 0 },
          { freq: 770, duration: 0.15, wave: 'sine', delay: 50 },
          { freq: 880, duration: 0.1, wave: 'triangle', delay: 100 }
        ], 0.1);
        break;
        
      case 'epicStart':
        createComplexSound([
          { freq: 330, duration: 0.4, wave: 'triangle', delay: 0 },
          { freq: 440, duration: 0.4, wave: 'sine', delay: 150 },
          { freq: 660, duration: 0.5, wave: 'triangle', delay: 300 },
          { freq: 880, duration: 0.6, wave: 'sine', delay: 450 }
        ], 0.3);
        break;
        
      default:
        createTone(440, 0.1);
    }
  } catch (error) {
    console.log('Audio not available:', error);
  }
};

const CrystalBallFortune = ({ walletData, onBack }) => {
  const [gameState, setGameState] = useState({
    betAmount: '1',
    selectedOrb: null,
    isRevealing: false,
    isApproving: false,
    lastResult: null,
    balance: '0',
    allowance: '0',
    playerLevel: 1,
    experience: 0,
    totalWins: 0,
    showRevealPopup: false,
    crystalVision: '🔮' // Current crystal display
  });

  const [gameStats, setGameStats] = useState({
    totalGames: 0,
    totalBets: '0',
    totalPayouts: '0',
    houseEdge: '50.0',
    effectiveMultiplier: '4.0',
    playersOnline: 0
  });

  // Admin state (same structure as LuckyDice)
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminData, setAdminData] = useState({
    houseBalance: '0',
    isPaused: false,
    minBet: '1',
    maxBet: '1000000',
    dividendEnabled: false,
    gameConfig: {
      noWinChance: 5000,
      orbChances: [1000, 1000, 1000, 1000, 1000],
      orbWinPayout: 450
    }
  });
  const [adminForm, setAdminForm] = useState({
    minBet: '1',
    maxBet: '1000000',
    fundAmount: '',
    withdrawAmount: '',
    dividendEnabled: false,
    noWinChance: '50.0',
    orbChances: ['10.0', '10.0', '10.0', '10.0', '10.0'],
    orbWinPayout: '4.5'
  });
  const [adminLoading, setAdminLoading] = useState(false);

  const [gameHistory, setGameHistory] = useState([]);
  const [animationState, setAnimationState] = useState('idle');
  const [visionResult, setVisionResult] = useState(null);
  const [particles, setParticles] = useState([]);
  const [connected, setConnected] = useState(!!walletData?.account);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [contracts, setContracts] = useState({
    crystalBall: null,
    blbToken: null
  });

  const crystalRef = useRef(null);
  
  // Orb mappings
  const orbSymbols = ['🔮', '⭐', '🌙', '⚡', '💫'];
  const orbNames = ['Crystal Orb', 'Star Orb', 'Moon Orb', 'Lightning Orb', 'Galaxy Orb'];

  const playGameSound = (type, volume = 0.3) => {
    if (soundEnabled) {
      playSound(type, volume);
    }
  };

  // Check if current wallet is admin (same as LuckyDice)
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (walletData?.account && contracts.crystalBall) {
        try {
          const [owner, admin] = await Promise.all([
            contracts.crystalBall.owner().catch(() => null),
            contracts.crystalBall.admin().catch(() => null)
          ]);
          
          const isOwner = owner && owner.toLowerCase() === walletData.account.toLowerCase();
          const isAdminRole = admin && admin.toLowerCase() === walletData.account.toLowerCase();
          const hasAdminAccess = isOwner || isAdminRole;
          
          setIsAdmin(hasAdminAccess);
          
          if (hasAdminAccess) {
            loadAdminData();
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      }
    };

    if (contracts.crystalBall) {
      checkAdminStatus();
    }
  }, [walletData?.account, contracts.crystalBall]);

  // Initialize contracts (same as LuckyDice)
  useEffect(() => {
    if (walletData?.provider && walletData?.account) {
      initializeContracts();
    } else {
      setContracts({ crystalBall: null, blbToken: null });
    }
  }, [walletData?.provider, walletData?.account]);

  const initializeContracts = async () => {
    try {
      const signer = walletData.provider.getSigner();
      
      const crystalBallContract = new ethers.Contract(
        CONTRACT_ADDRESSES.CRYSTALBALL,
        CRYSTALBALL_ABI,
        signer
      );
      
      const blbTokenContract = new ethers.Contract(
        CONTRACT_ADDRESSES.BLB_TOKEN,
        BLB_TOKEN_ABI,
        signer
      );

      setContracts({
        crystalBall: crystalBallContract,
        blbToken: blbTokenContract
      });

      setTimeout(() => {
        loadGameData(crystalBallContract);
        loadBalance(blbTokenContract, crystalBallContract);
      }, 500);

    } catch (error) {
      console.error('Error initializing contracts:', error);
      setContracts({ crystalBall: null, blbToken: null });
    }
  };

  // Load admin data (adapted from LuckyDice)
  const loadAdminData = async (contract = contracts.crystalBall) => {
    try {
      if (!contract) return;

      const [houseBalance, isPaused, minBet, maxBet, config, dividendInfo] = await Promise.all([
        contract.houseBalance().catch(() => ethers.BigNumber.from(0)),
        contract.paused().catch(() => false),
        contract.minBet().catch(() => ethers.utils.parseEther('1')),
        contract.maxBet().catch(() => ethers.utils.parseEther('1000000')),
        contract.getGameConfig().catch(() => ({
          noWinChance: 5000,
          orbChances: [1000, 1000, 1000, 1000, 1000],
          orbWinPayout: 450
        })),
        contract.getDividendInfo().catch(() => [ethers.constants.AddressZero, false])
      ]);

      const adminInfo = {
        houseBalance: ethers.utils.formatEther(houseBalance),
        isPaused,
        minBet: ethers.utils.formatEther(minBet),
        maxBet: ethers.utils.formatEther(maxBet),
        dividendEnabled: dividendInfo[1],
        gameConfig: {
          noWinChance: parseInt(config.noWinChance),
          orbChances: config.orbChances.map(c => parseInt(c)),
          orbWinPayout: parseInt(config.orbWinPayout)
        }
      };

      setAdminData(adminInfo);
      setAdminForm({
        minBet: adminInfo.minBet,
        maxBet: adminInfo.maxBet,
        fundAmount: '',
        withdrawAmount: '',
        dividendEnabled: adminInfo.dividendEnabled,
        noWinChance: (adminInfo.gameConfig.noWinChance / 100).toString(),
        orbChances: adminInfo.gameConfig.orbChances.map(c => (c / 100).toString()),
        orbWinPayout: (adminInfo.gameConfig.orbWinPayout / 100).toString()
      });

    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  // Particle effects for wins (same as LuckyDice)
  useEffect(() => {
    if (animationState === 'result' && visionResult?.won) {
      const payout = parseFloat(visionResult.netPayout);
      const particleCount = payout >= 1000 ? 30 : payout >= 100 ? 20 : 15;
      const newParticles = Array.from({ length: particleCount }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 6 + 3,
        life: 100,
        velocity: {
          x: (Math.random() - 0.5) * 3,
          y: Math.random() * -2 - 1
        }
      }));
      setParticles(newParticles);
      
      const interval = setInterval(() => {
        setParticles(prev => prev.map(p => ({ 
          ...p, 
          life: p.life - 2,
          x: p.x + p.velocity.x,
          y: p.y + p.velocity.y
        })).filter(p => p.life > 0));
      }, 50);
      
      setTimeout(() => clearInterval(interval), 4000);
    }
  }, [animationState, visionResult]);

  // Connection state (same as LuckyDice)
  useEffect(() => {
    if (walletData?.account) {
      setConnected(true);
    }
  }, [walletData]);

  // Load game data from contract (adapted for Crystal Ball)
  const loadGameData = async (contract = contracts.crystalBall) => {
    try {
      if (!contract) {
        setGameStats({
          totalGames: 0,
          totalBets: '0',
          totalPayouts: '0',
          houseEdge: '50.0',
          effectiveMultiplier: '4.0',
          playersOnline: Math.floor(Math.random() * 150) + 30
        });
        return;
      }

      let stats, recentGames, houseEdge, effectiveMultiplier;

      try {
        stats = await contract.getGameStats();
      } catch (e) {
        stats = {
          totalGames: ethers.BigNumber.from(0),
          totalBets: ethers.BigNumber.from(0),
          totalPayoutsAmount: ethers.BigNumber.from(0)
        };
      }

      try {
        recentGames = await contract.getRecentGames(5);
      } catch (e) {
        recentGames = [];
      }

      try {
        houseEdge = await contract.calculateHouseEdge();
      } catch (e) {
        houseEdge = ethers.BigNumber.from(5000);
      }

      try {
        effectiveMultiplier = await contract.getEffectiveMultiplier();
      } catch (e) {
        effectiveMultiplier = ethers.BigNumber.from(450);
      }

      setGameStats({
        totalGames: parseInt(stats.totalGames.toString()),
        totalBets: ethers.utils.formatEther(stats.totalBets),
        totalPayouts: ethers.utils.formatEther(stats.totalPayoutsAmount),
        houseEdge: (parseInt(houseEdge.toString()) / 100).toString(),
        effectiveMultiplier: (parseInt(effectiveMultiplier.toString()) / 100).toString(),
        playersOnline: Math.floor(Math.random() * 150) + 30
      });

      if (recentGames && recentGames.length > 0) {
        const formattedHistory = recentGames.slice(0, 5).map(game => ({
          playerChoice: parseInt(game.playerChoice),
          result: parseInt(game.result),
          won: game.won,
          betAmount: ethers.utils.formatEther(game.betAmount),
          netPayout: ethers.utils.formatEther(game.netPayout),
          timestamp: parseInt(game.timestamp) * 1000
        }));
        setGameHistory(formattedHistory);
      }

    } catch (error) {
      console.log('Using default game data');
      setGameStats({
        totalGames: 0,
        totalBets: '0',
        totalPayouts: '0',
        houseEdge: '50.0',
        effectiveMultiplier: '4.0',
        playersOnline: Math.floor(Math.random() * 150) + 30
      });
    }
  };

  // Load BLB balance (same as LuckyDice)
  const loadBalance = async (tokenContract = contracts.blbToken, gameContract = contracts.crystalBall) => {
    try {
      if (!tokenContract || !gameContract || !walletData?.account) {
        setGameState(prev => ({
          ...prev,
          balance: '1000',
          allowance: '0'
        }));
        return;
      }

      let balance, allowance;

      try {
        balance = await tokenContract.balanceOf(walletData.account);
      } catch (e) {
        balance = ethers.utils.parseEther('1000');
      }

      try {
        allowance = await tokenContract.allowance(walletData.account, gameContract.address);
      } catch (e) {
        allowance = ethers.BigNumber.from(0);
      }

      setGameState(prev => ({
        ...prev,
        balance: ethers.utils.formatEther(balance),
        allowance: ethers.utils.formatEther(allowance)
      }));

    } catch (error) {
      console.log('Using demo balance');
      setGameState(prev => ({
        ...prev,
        balance: '1000',
        allowance: '0'
      }));
    }
  };

  // Admin Functions (same structure as LuckyDice, adapted for Crystal Ball)
  const handlePauseGame = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.crystalBall) {
        const tx = await contracts.crystalBall.pauseGame();
        await tx.wait();
        setSuccessMessage('✅ Crystal realm sealed successfully!');
      } else {
        setSuccessMessage('✅ Crystal realm sealed! (Demo)');
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to seal realm: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUnpauseGame = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.crystalBall) {
        const tx = await contracts.crystalBall.unpauseGame();
        await tx.wait();
        setSuccessMessage('✅ Crystal realm opened successfully!');
      } else {
        setSuccessMessage('✅ Crystal realm opened! (Demo)');
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to open realm: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleFundHouse = async () => {
    try {
      if (!adminForm.fundAmount || parseFloat(adminForm.fundAmount) <= 0) {
        setErrorMessage('Please enter a valid fund amount');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');
      const fundAmountWei = ethers.utils.parseEther(adminForm.fundAmount);

      if (contracts.crystalBall && contracts.blbToken) {
        const allowance = await contracts.blbToken.allowance(walletData.account, contracts.crystalBall.address);
        if (allowance.lt(fundAmountWei)) {
          const approveTx = await contracts.blbToken.approve(contracts.crystalBall.address, ethers.constants.MaxUint256);
          await approveTx.wait();
        }

        const tx = await contracts.crystalBall.fundHouse(fundAmountWei);
        await tx.wait();
        setSuccessMessage(`✅ Crystal treasury funded with ${adminForm.fundAmount} BLB!`);
      } else {
        setSuccessMessage(`✅ Crystal treasury funded with ${adminForm.fundAmount} BLB! (Demo)`);
      }

      setAdminForm(prev => ({ ...prev, fundAmount: '' }));
      await Promise.all([loadAdminData(), loadBalance()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to fund treasury: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleWithdrawHouse = async () => {
    try {
      if (!adminForm.withdrawAmount || parseFloat(adminForm.withdrawAmount) <= 0) {
        setErrorMessage('Please enter a valid withdraw amount');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');
      const withdrawAmountWei = ethers.utils.parseEther(adminForm.withdrawAmount);

      if (contracts.crystalBall) {
        const tx = await contracts.crystalBall.withdrawHouse(withdrawAmountWei);
        await tx.wait();
        setSuccessMessage(`✅ Withdrew ${adminForm.withdrawAmount} BLB from crystal treasury!`);
      } else {
        setSuccessMessage(`✅ Withdrew ${adminForm.withdrawAmount} BLB from crystal treasury! (Demo)`);
      }

      setAdminForm(prev => ({ ...prev, withdrawAmount: '' }));
      await Promise.all([loadAdminData(), loadBalance()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to withdraw from treasury: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleToggleDividend = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.crystalBall) {
        const tx = await contracts.crystalBall.toggleDividend(!adminForm.dividendEnabled);
        await tx.wait();
        setSuccessMessage(`✅ Mystical dividends ${!adminForm.dividendEnabled ? 'enabled' : 'disabled'}!`);
      } else {
        setSuccessMessage(`✅ Mystical dividends ${!adminForm.dividendEnabled ? 'enabled' : 'disabled'}! (Demo)`);
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to toggle dividends: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUpdateWinChances = async () => {
    try {
      const noWinChance = Math.round(parseFloat(adminForm.noWinChance) * 100);
      const orbChances = adminForm.orbChances.map(c => Math.round(parseFloat(c) * 100));
      
      const totalChances = noWinChance + orbChances.reduce((sum, c) => sum + c, 0);
      if (totalChances !== 10000) {
        setErrorMessage('Total chances must equal 100%');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.crystalBall) {
        const tx = await contracts.crystalBall.updateWinChances(noWinChance, orbChances);
        await tx.wait();
        setSuccessMessage('✅ Orb chances updated successfully!');
      } else {
        setSuccessMessage('✅ Orb chances updated successfully! (Demo)');
      }

      await Promise.all([loadAdminData(), loadGameData()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to update chances: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUpdatePayout = async () => {
    try {
      const payout = Math.round(parseFloat(adminForm.orbWinPayout) * 100);
      
      if (payout < 200 || payout > 600) {
        setErrorMessage('Payout must be between 2x-6x');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.crystalBall) {
        const tx = await contracts.crystalBall.updatePayout(payout);
        await tx.wait();
        setSuccessMessage('✅ Crystal payout updated successfully!');
      } else {
        setSuccessMessage('✅ Crystal payout updated successfully! (Demo)');
      }

      await Promise.all([loadAdminData(), loadGameData()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to update payout: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleSetBetLimits = async () => {
    try {
      const minBet = parseFloat(adminForm.minBet);
      const maxBet = parseFloat(adminForm.maxBet);

      if (minBet <= 0 || maxBet <= 0 || minBet >= maxBet) {
        setErrorMessage('Invalid bet limits');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.crystalBall) {
        const minBetWei = ethers.utils.parseEther(adminForm.minBet);
        const maxBetWei = ethers.utils.parseEther(adminForm.maxBet);
        
        const tx = await contracts.crystalBall.setBetLimits(minBetWei, maxBetWei);
        await tx.wait();
        setSuccessMessage(`✅ Bet limits updated! Min: ${adminForm.minBet} BLB, Max: ${adminForm.maxBet} BLB`);
      } else {
        setSuccessMessage(`✅ Bet limits updated! Min: ${adminForm.minBet} BLB, Max: ${adminForm.maxBet} BLB (Demo)`);
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to set bet limits: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleEmergencyWithdraw = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.crystalBall) {
        const tx = await contracts.crystalBall.emergencyWithdrawAll();
        await tx.wait();
        setSuccessMessage('✅ Emergency crystal extraction completed!');
      } else {
        setSuccessMessage('✅ Emergency crystal extraction completed! (Demo)');
      }

      await Promise.all([loadAdminData(), loadBalance()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to emergency withdraw: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  // Handle crystal reveal (main game function)
  const handleReveal = async () => {
    if (gameState.selectedOrb === null) {
      setErrorMessage('Please select an orb to reveal your destiny');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');

      const betAmount = parseFloat(gameState.betAmount);
      const balance = parseFloat(gameState.balance);

      if (betAmount <= 0 || betAmount > balance) {
        setErrorMessage('Invalid bet amount');
        return;
      }

      playGameSound('epicStart', 0.5);
      
      setGameState(prev => ({ 
        ...prev, 
        isRevealing: true, 
        showRevealPopup: true 
      }));
      setAnimationState('revealing');

      // Start crystal animation
      if (crystalRef.current) {
        crystalRef.current.style.animation = 'crystalReveal 1s ease-in-out infinite';
      }

      setTimeout(() => playGameSound('crystalReveal', 0.6), 300);

      // If contracts available, use real blockchain
      if (contracts.crystalBall && contracts.blbToken) {
        try {
          const betAmountWei = ethers.utils.parseEther(gameState.betAmount);
          const tx = await contracts.crystalBall.selectOrb(gameState.selectedOrb, betAmountWei);
          
          setSuccessMessage('Transaction submitted! Consulting the crystal...');
          
          const receipt = await tx.wait();
          
          const gamePlayedEvent = receipt.events?.find(event => event.event === 'GamePlayed');
          
          if (gamePlayedEvent) {
            const [player, betAmount, playerChoice, result, grossPayout, netPayout, won] = gamePlayedEvent.args;
            
            const gameResult = {
              player,
              betAmount: ethers.utils.formatEther(betAmount),
              playerChoice: parseInt(playerChoice),
              result: parseInt(result),
              grossPayout: ethers.utils.formatEther(grossPayout),
              netPayout: ethers.utils.formatEther(netPayout),
              won,
              timestamp: Date.now()
            };

            setTimeout(() => {
              handleVisionResult(gameResult, true);
            }, 2000);
            
            await Promise.all([loadGameData(), loadBalance()]);
            return;
          }
        } catch (contractError) {
          console.error('Contract transaction failed:', contractError);
          
          setErrorMessage('Wallet connection low bandwidth. Refresh and try again');
          setAnimationState('idle');
          setGameState(prev => ({ 
            ...prev, 
            isRevealing: false, 
            showRevealPopup: false 
          }));
          if (crystalRef.current) {
            crystalRef.current.style.animation = '';
          }
          return;
        }
      }

      // If no contracts available, show connection error
      setErrorMessage('Smart contract not connected. Please refresh the page and connect your wallet.');
      setAnimationState('idle');
      setGameState(prev => ({ 
        ...prev, 
        isRevealing: false, 
        showRevealPopup: false 
      }));
      if (crystalRef.current) {
        crystalRef.current.style.animation = '';
      }

    } catch (error) {
      console.error('Game error:', error);
      setErrorMessage('Transaction failed. Please refresh and try again.');
      setAnimationState('idle');
      setGameState(prev => ({ 
        ...prev, 
        isRevealing: false, 
        showRevealPopup: false 
      }));
      if (crystalRef.current) {
        crystalRef.current.style.animation = '';
      }
    }
  };

  const handleVisionResult = (visionResult, isReal) => {
    setVisionResult(visionResult);
    setAnimationState('result');

    // Stop crystal animation and show result
    if (crystalRef.current) {
      crystalRef.current.style.animation = '';
    }

    // Update crystal vision display
    setGameState(prev => ({
      ...prev,
      crystalVision: visionResult.result < 5 ? orbSymbols[visionResult.result] : '💀'
    }));

    setTimeout(() => {
      if (visionResult.won) {
        const payout = parseFloat(visionResult.netPayout);
        if (payout >= 100) {
          playGameSound('epicWin', 0.8);
          setSuccessMessage(`🔮 LEGENDARY CRYSTAL VISION! You won ${formatNumber(visionResult.netPayout)} BLB! 🔮`);
        } else if (payout >= 20) {
          playGameSound('bigWin', 0.6);
          setSuccessMessage(`⭐ Mystical Victory! You won ${formatNumber(visionResult.netPayout)} BLB!`);
        } else {
          playGameSound('smallWin', 0.4);
          setSuccessMessage(`🌟 You won ${formatNumber(visionResult.netPayout)} BLB!`);
        }
      } else {
        playGameSound('lose', 0.3);
        if (visionResult.result === 5) {
          setErrorMessage(`🔮 The crystal shows darkness! Lost ${formatNumber(visionResult.betAmount)} BLB`);
        } else {
          setErrorMessage(`🔮 The orb chose ${orbNames[visionResult.result]}! Lost ${formatNumber(visionResult.betAmount)} BLB`);
        }
      }
    }, 600);

    // Update local state for real games
    if (isReal) {
      setGameState(prev => ({
        ...prev,
        isRevealing: false,
        totalWins: visionResult.won ? prev.totalWins + 1 : prev.totalWins,
        experience: prev.experience + (visionResult.won ? 120 : 25),
        playerLevel: Math.floor((prev.experience + (visionResult.won ? 120 : 25)) / 250) + 1
      }));

      setGameHistory(prev => [visionResult, ...prev.slice(0, 4)]);
    }

    // Reset after showing result
    setTimeout(() => {
      setAnimationState('idle');
      setVisionResult(null);
      setGameState(prev => ({ 
        ...prev, 
        isRevealing: false,
        showRevealPopup: false,
        selectedOrb: null,
        crystalVision: '🔮'
      }));
      setErrorMessage('');
      setSuccessMessage('');
    }, 4000);
  };

  // Handle token approval (same as LuckyDice)
  const handleApprove = async () => {
    try {
      setErrorMessage('');
      playGameSound('buttonClick', 0.3);
      setGameState(prev => ({ ...prev, isApproving: true }));

      if (contracts.blbToken && contracts.crystalBall) {
        const maxAmount = ethers.constants.MaxUint256;
        const tx = await contracts.blbToken.approve(contracts.crystalBall.address, maxAmount);
        await tx.wait();
        await loadBalance();
        setSuccessMessage('✅ Mystical approval granted!');
      } else {
        setGameState(prev => ({ ...prev, allowance: '999999999' }));
        setSuccessMessage('✅ Mystical approval granted!');
      }

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Approval error:', error);
      setErrorMessage('Approval failed: ' + error.message);
    } finally {
      setGameState(prev => ({ ...prev, isApproving: false }));
    }
  };

  const handleOrbSelect = (orbIndex) => {
    playGameSound('orbSelect', 0.3);
    setGameState(prev => ({ ...prev, selectedOrb: orbIndex }));
  };

  const needsApproval = () => {
    return parseFloat(gameState.allowance) < parseFloat(gameState.betAmount);
  };

  const canPlay = () => {
    return connected && 
           gameState.selectedOrb !== null &&
           parseFloat(gameState.balance) >= parseFloat(gameState.betAmount) &&
           !needsApproval() &&
           !gameState.isRevealing &&
           !adminData.isPaused;
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(2);
  };

  const getMultiplier = () => {
    return (parseFloat(adminData.gameConfig?.orbWinPayout || 450) / 100).toFixed(1) + 'x';
  };

  // Connect Screen (same style as LuckyDice but with crystal theme)
  if (!connected) {
    return (
      <div className="crystalball-mystic-realm">
        <div className="crystalball-connect-screen">
          <div className="crystalball-connect-icon">🔮</div>
          <h3 className="crystalball-connect-title">🔮 ENTER THE MYSTIC REALM</h3>
          <p className="crystalball-connect-desc">Connect your wallet to consult the ancient crystal orbs</p>
          <button 
            className="crystalball-action-btn crystalball-reveal-btn"
            onClick={() => {
              playGameSound('buttonClick', 0.4);
              setConnected(true);
              setGameState(prev => ({ ...prev, balance: '1000' }));
            }}
            onMouseEnter={() => playGameSound('buttonHover', 0.2)}
          >
            🔗 CONNECT WALLET
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="crystalball-mystic-realm">
      {/* Header */}
      <div className="crystalball-header-bar">
        <button 
          className="crystalball-back-btn" 
          onClick={() => {
            playGameSound('buttonClick', 0.3);
            if (onBack) onBack();
          }}
          onMouseEnter={() => playGameSound('buttonHover', 0.2)}
        >
          ← EXIT REALM
        </button>
        
        <div className="crystalball-title-section">
          <h2 className="crystalball-main-title">CRYSTAL BALL FORTUNE</h2>
          <div className="crystalball-subtitle">
            Ancient Orbs of Destiny • {getMultiplier()} Multiplier
          </div>
          {adminData.isPaused && (
            <div className="crystalball-paused-indicator">
              ⚠️ REALM SEALED
            </div>
          )}
        </div>
        
        <div className="crystalball-balance-display">
          <div className="crystalball-balance-amount">
            💎 {formatNumber(gameState.balance)} BLB
          </div>
          <div className="crystalball-player-level">
            LVL {gameState.playerLevel}
          </div>
          
          {isAdmin && (
            <button
              className="crystalball-admin-btn"
              onClick={() => {
                setShowAdminPanel(!showAdminPanel);
                playGameSound('buttonClick', 0.3);
              }}
              title="Mystic Master Panel"
            >
              🔮
            </button>
          )}
          
          <button
            className="crystalball-sound-btn"
            onClick={() => {
              setSoundEnabled(!soundEnabled);
              playGameSound('buttonClick', 0.2);
            }}
            title={soundEnabled ? 'Silence Crystals' : 'Awaken Crystals'}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
        </div>
      </div>

      {/* Admin Panel */}
      {isAdmin && showAdminPanel && (
        <div className="crystalball-admin-overlay">
          <div className="crystalball-admin-panel">
            <div className="crystalball-admin-header">
              <h2 className="crystalball-admin-title">
                🔮 CRYSTAL MASTER PANEL
              </h2>
              <button
                onClick={() => setShowAdminPanel(false)}
                className="crystalball-admin-close"
              >
                ✕
              </button>
            </div>

            <div className="crystalball-admin-grid">
              {/* Realm Status */}
              <div className="crystalball-control-panel">
                <label className="crystalball-control-label">🔮 REALM STATUS</label>
                <div className="crystalball-admin-status">
                  <div className="crystalball-status-info">
                    Status: <span className={adminData.isPaused ? 'status-sealed' : 'status-mystical'}>
                      {adminData.isPaused ? '⚠️ SEALED' : '🔮 MYSTICAL'}
                    </span>
                  </div>
                  <div className="crystalball-status-buttons">
                    <button
                      className="crystalball-action-btn pause-btn"
                      onClick={handlePauseGame}
                      disabled={adminLoading || adminData.isPaused}
                    >
                      🔒 SEAL REALM
                    </button>
                    <button
                      className="crystalball-action-btn unpause-btn"
                      onClick={handleUnpauseGame}
                      disabled={adminLoading || !adminData.isPaused}
                    >
                      🔮 OPEN REALM
                    </button>
                  </div>
                </div>
              </div>

              {/* Crystal Treasury */}
              <div className="crystalball-control-panel">
                <label className="crystalball-control-label">💎 CRYSTAL TREASURY</label>
                <div className="crystalball-treasury-amount">
                  ✨ {formatNumber(adminData.houseBalance)} BLB
                </div>
                <button
                  className="crystalball-action-btn emergency-btn"
                  onClick={handleEmergencyWithdraw}
                  disabled={adminLoading}
                >
                  {adminLoading ? '⏳ EXTRACTING...' : '🚨 EMERGENCY EXTRACTION'}
                </button>
              </div>

              {/* Fund Treasury */}
              <div className="crystalball-control-panel">
                <label className="crystalball-control-label">💰 FUND TREASURY</label>
                <div className="crystalball-fund-controls">
                  <input
                    className="crystalball-stake-input"
                    type="number"
                    placeholder="Amount to fund crystal treasury"
                    value={adminForm.fundAmount}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, fundAmount: e.target.value }))}
                    min="1"
                    step="1"
                  />
                  <button
                    className="crystalball-action-btn fund-btn"
                    onClick={handleFundHouse}
                    disabled={adminLoading || !adminForm.fundAmount}
                  >
                    {adminLoading ? '⏳ CHANNELING...' : '💎 FUND TREASURY'}
                  </button>
                </div>
              </div>

              {/* Withdraw Treasury */}
              <div className="crystalball-control-panel">
                <label className="crystalball-control-label">💸 WITHDRAW TREASURY</label>
                <div className="crystalball-withdraw-controls">
                  <input
                    className="crystalball-stake-input"
                    type="number"
                    placeholder="Amount to withdraw"
                    value={adminForm.withdrawAmount}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, withdrawAmount: e.target.value }))}
                    min="1"
                    step="1"
                  />
                  <button
                    className="crystalball-action-btn withdraw-btn"
                    onClick={handleWithdrawHouse}
                    disabled={adminLoading || !adminForm.withdrawAmount}
                  >
                    {adminLoading ? '⏳ EXTRACTING...' : '💸 WITHDRAW'}
                  </button>
                </div>
              </div>

              {/* Bet Limits */}
              <div className="crystalball-control-panel">
                <label className="crystalball-control-label">🎯 MYSTICAL LIMITS</label>
                <div className="crystalball-limits-controls">
                  <div className="crystalball-limits-inputs">
                    <div className="crystalball-limit-input">
                      <label className="crystalball-input-label">MIN BET</label>
                      <input
                        className="crystalball-stake-input"
                        type="number"
                        value={adminForm.minBet}
                        onChange={(e) => setAdminForm(prev => ({ ...prev, minBet: e.target.value }))}
                        min="1"
                        step="1"
                      />
                    </div>
                    <div className="crystalball-limit-input">
                      <label className="crystalball-input-label">MAX BET</label>
                      <input
                        className="crystalball-stake-input"
                        type="number"
                        value={adminForm.maxBet}
                        onChange={(e) => setAdminForm(prev => ({ ...prev, maxBet: e.target.value }))}
                        min="1"
                        step="1"
                      />
                    </div>
                  </div>
                  <button
                    className="crystalball-action-btn limits-btn"
                    onClick={handleSetBetLimits}
                    disabled={adminLoading}
                  >
                    {adminLoading ? '⏳ CONFIGURING...' : '🎯 SET LIMITS'}
                  </button>
                </div>
              </div>

              {/* Mystical Dividends */}
              <div className="crystalball-control-panel">
                <label className="crystalball-control-label">✨ MYSTICAL DIVIDENDS</label>
                <div className="crystalball-dividend-controls">
                  <div className="crystalball-dividend-status">
                    Status: <span className={adminForm.dividendEnabled ? 'dividend-flowing' : 'dividend-dormant'}>
                      {adminForm.dividendEnabled ? '✅ FLOWING' : '❌ DORMANT'}
                    </span>
                  </div>
                  <button
                    className={`crystalball-action-btn ${adminForm.dividendEnabled ? 'dividend-disable-btn' : 'dividend-enable-btn'}`}
                    onClick={handleToggleDividend}
                    disabled={adminLoading}
                  >
                    {adminLoading ? '⏳ CHANNELING...' : (adminForm.dividendEnabled ? '❌ STOP FLOW' : '✅ ENABLE FLOW')}
                  </button>
                </div>
              </div>

              {/* Crystal Configuration */}
              <div className="crystalball-control-panel crystalball-config-panel">
                <label className="crystalball-control-label">🔮 CRYSTAL ORB CONFIGURATION</label>
                <div className="crystalball-config-inputs">
                  <div className="crystalball-config-input">
                    <label className="crystalball-input-label">HOUSE WIN (%)</label>
                    <input
                      className="crystalball-stake-input"
                      type="number"
                      value={adminForm.noWinChance}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, noWinChance: e.target.value }))}
                      min="0"
                      max="90"
                      step="0.1"
                      placeholder="50.0"
                    />
                  </div>
                  {adminForm.orbChances.map((chance, index) => (
                    <div key={index} className="crystalball-config-input">
                      <label className="crystalball-input-label">
                        {orbNames[index].toUpperCase()} (%)
                      </label>
                      <input
                        className="crystalball-stake-input"
                        type="number"
                        value={chance}
                        onChange={(e) => {
                          const newChances = [...adminForm.orbChances];
                          newChances[index] = e.target.value;
                          setAdminForm(prev => ({ ...prev, orbChances: newChances }));
                        }}
                        min="0"
                        max="50"
                        step="0.1"
                        placeholder="10.0"
                      />
                    </div>
                  ))}
                  <div className="crystalball-config-input">
                    <label className="crystalball-input-label">CRYSTAL PAYOUT (x)</label>
                    <input
                      className="crystalball-stake-input"
                      type="number"
                      value={adminForm.orbWinPayout}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, orbWinPayout: e.target.value }))}
                      min="2"
                      max="6"
                      step="0.1"
                      placeholder="4.5"
                    />
                  </div>
                </div>

                <div className="crystalball-config-info">
                  <strong>Total Percentage:</strong> {(
                    parseFloat(adminForm.noWinChance || 0) + 
                    adminForm.orbChances.reduce((sum, c) => sum + parseFloat(c || 0), 0)
                  ).toFixed(1)}% 
                  <br />
                  <strong>House Edge:</strong> <span className="house-edge-value">{parseFloat(adminForm.noWinChance || 0).toFixed(1)}%</span>
                  <br />
                  {Math.abs((parseFloat(adminForm.noWinChance || 0) + adminForm.orbChances.reduce((sum, c) => sum + parseFloat(c || 0), 0)) - 100) > 0.1 && (
                    <span className="config-warning">⚠️ Must equal 100%</span>
                  )}
                </div>

                <div className="crystalball-config-buttons">
                  <button
                    className="crystalball-action-btn update-chances-btn"
                    onClick={handleUpdateWinChances}
                    disabled={adminLoading || Math.abs((parseFloat(adminForm.noWinChance || 0) + adminForm.orbChances.reduce((sum, c) => sum + parseFloat(c || 0), 0)) - 100) > 0.1}
                  >
                    {adminLoading ? '⏳ CHANNELING...' : '🔮 UPDATE CHANCES'}
                  </button>
                  <button
                    className="crystalball-action-btn update-payout-btn"
                    onClick={handleUpdatePayout}
                    disabled={adminLoading}
                  >
                    {adminLoading ? '⏳ ENCHANTING...' : '💎 UPDATE PAYOUT'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {errorMessage && (
        <div className="crystalball-message crystalball-error-msg">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="crystalball-message crystalball-success-msg">
          {successMessage}
        </div>
      )}

      <div className="crystalball-game-layout">
        {/* Main Game Zone */}
        <div className="crystalball-game-zone">
          {/* Crystal Ball Table */}
          <div className="crystalball-table-container">
            <div className="crystalball-crystal-container">
              <div 
                ref={crystalRef}
                className={`crystalball-crystal-main ${gameState.isRevealing ? 'revealing' : ''}`}
                onClick={() => !gameState.isRevealing && playGameSound('buttonClick', 0.3)}
              >
                <div className="crystalball-crystal-vision">
                  {gameState.crystalVision}
                </div>
              </div>
            </div>

            {/* Victory Particles */}
            {particles.map(particle => (
              <div
                key={particle.id}
                className="crystalball-particle"
                style={{
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  opacity: particle.life / 100
                }}
              />
            ))}

            {/* Orb Selection */}
            <div className="crystalball-orb-selection">
              {orbSymbols.map((symbol, index) => (
                <div
                  key={index}
                  className={`crystalball-orb-option ${gameState.selectedOrb === index ? 'selected' : ''}`}
                  onClick={() => !gameState.isRevealing && handleOrbSelect(index)}
                  onMouseEnter={() => playGameSound('buttonHover', 0.2)}
                >
                  <div className="crystalball-orb-symbol">{symbol}</div>
                  <div className="crystalball-orb-name">{orbNames[index]}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Reveal Popup */}
          {gameState.showRevealPopup && (
            <div className="crystalball-reveal-popup-overlay">
              <div className="crystalball-reveal-popup">
                <div className="crystalball-popup-header">
                  <h2>🔮 CONSULTING THE CRYSTAL ORBS!</h2>
                  <div className="crystalball-bet-info">
                    <span>Stake: {formatNumber(gameState.betAmount)} BLB</span>
                    <span>Chosen Orb: {gameState.selectedOrb !== null ? orbNames[gameState.selectedOrb] : 'None'}</span>
                    <span>Multiplier: {getMultiplier()}</span>
                  </div>
                </div>

                <div className="crystalball-popup-crystal">
                  <div 
                    className={`crystalball-popup-crystal-orb ${
                      animationState === 'revealing' ? 'revealing-animation' : ''
                    }`}
                  >
                    {gameState.crystalVision}
                  </div>
                </div>

                {/* Result Display in Popup */}
                {animationState === 'result' && visionResult && (
                  <div className="crystalball-popup-result-display">
                    <div className={`crystalball-popup-result-content ${
                      visionResult.won ? 'crystalball-popup-victory' : 'crystalball-popup-defeat'
                    }`}>
                      <div className="crystalball-popup-result-icon">
                        {visionResult.won ? '🔮' : '💀'}
                      </div>
                      <h3 className="crystalball-popup-result-title">
                        {visionResult.won 
                          ? '🌟 MYSTICAL VICTORY!'
                          : '🔮 CRYSTAL REVEALS LOSS'
                        }
                      </h3>
                      <div className="crystalball-popup-result-amount">
                        {visionResult.won ? `+${formatNumber(visionResult.netPayout)}` : `-${formatNumber(visionResult.betAmount)}`} BLB
                      </div>
                      <div className="crystalball-popup-orb-result">
                        Winning Orb: {visionResult.result < 5 ? orbNames[visionResult.result] : 'House Darkness'}
                      </div>
                      <div className="crystalball-popup-result-desc">
                        {visionResult.won 
                          ? `You chose ${orbNames[visionResult.playerChoice]} and the crystal revealed ${visionResult.result < 5 ? orbNames[visionResult.result] : 'darkness'}!`
                          : visionResult.result === visionResult.playerChoice
                            ? 'The mystical forces were not aligned!'
                            : `You chose ${orbNames[visionResult.playerChoice]} but the crystal revealed ${visionResult.result < 5 ? orbNames[visionResult.result] : 'darkness'}`
                        }
                      </div>
                    </div>
                  </div>
                )}

                {/* Revealing indicator */}
                {animationState === 'revealing' && (
                  <div className="crystalball-revealing-indicator">
                    <div className="crystalball-revealing-text">🔮 CRYSTAL REVEALING...</div>
                    <div className="crystalball-revealing-subtext">The ancient orbs are aligning...</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Game Controls */}
          <div className="crystalball-controls">
            <div className="crystalball-control-panel">
              <label className="crystalball-control-label">💰 MYSTICAL STAKE</label>
              <div className="crystalball-stake-section">
                <input
                className="crystalball-stake-input"
                  type="number"
                  value={gameState.betAmount}
                  onChange={(e) => setGameState(prev => ({ ...prev, betAmount: e.target.value }))}
                  disabled={gameState.isRevealing || adminData.isPaused}
                  min={adminData.minBet}
                  max={Math.min(parseFloat(gameState.balance), parseFloat(adminData.maxBet))}
                  step="1"
                  onFocus={() => playGameSound('buttonClick', 0.1)}
                />
                <div className="crystalball-preset-buttons">
                  {['1', '10', '100'].map(amount => (
                    <button
                      key={amount}
                      onClick={() => {
                        playGameSound('buttonClick', 0.2);
                        setGameState(prev => ({ ...prev, betAmount: amount }));
                      }}
                      onMouseEnter={() => playGameSound('buttonHover', 0.1)}
                      disabled={gameState.isRevealing || adminData.isPaused}
                      className="crystalball-preset-btn"
                    >
                      {amount}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      playGameSound('buttonClick', 0.2);
                      const maxAmount = Math.min(parseFloat(gameState.balance), parseFloat(adminData.maxBet));
                      setGameState(prev => ({ ...prev, betAmount: Math.floor(maxAmount).toString() }));
                    }}
                    onMouseEnter={() => playGameSound('buttonHover', 0.1)}
                    disabled={gameState.isRevealing || adminData.isPaused}
                    className="crystalball-preset-btn max-btn"
                  >
                    MAX
                  </button>
                </div>
              </div>
            </div>

            <div className="crystalball-control-panel">
              {adminData.isPaused ? (
                <button
                  className="crystalball-action-btn paused-btn"
                  disabled={true}
                >
                  ⚠️ REALM SEALED
                </button>
              ) : needsApproval() ? (
                <button
                  className="crystalball-action-btn crystalball-approve-btn"
                  onClick={handleApprove}
                  onMouseEnter={() => playGameSound('buttonHover', 0.2)}
                  disabled={gameState.isApproving}
                >
                  {gameState.isApproving ? '⏳ APPROVING...' : '🔓 APPROVE BLB'}
                </button>
              ) : (
                <button
                  className="crystalball-action-btn crystalball-reveal-btn"
                  onClick={handleReveal}
                  onMouseEnter={() => playGameSound('buttonHover', 0.2)}
                  disabled={!canPlay()}
                >
                  {gameState.isRevealing ? '🌀 REVEALING...' : '🔮 REVEAL DESTINY'}
                </button>
              )}
            </div>
          </div>

          {/* Potential Win Display */}
          {gameState.selectedOrb !== null && !gameState.isRevealing && !adminData.isPaused && (
            <div className="crystalball-potential-win">
              🔮 Chosen Orb: {orbNames[gameState.selectedOrb]} • Potential Win: {formatNumber((parseFloat(gameState.betAmount) * parseFloat(getMultiplier().replace('x', ''))).toString())} BLB
            </div>
          )}
        </div>

        {/* Stats Sidebar */}
        <div className="crystalball-stats-sidebar">
          <div className="crystalball-stats-panel">
            <h3 className="crystalball-stats-title">🔮 MYSTIC STATUS</h3>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">Level</span>
              <span className="crystalball-stats-value">{gameState.playerLevel}</span>
            </div>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">Experience</span>
              <span className="crystalball-stats-value">{formatNumber(gameState.experience)}</span>
            </div>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">Total Victories</span>
              <span className="crystalball-stats-value">{gameState.totalWins}</span>
            </div>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">Rank</span>
              <span className="crystalball-stats-value">Crystal Seer</span>
            </div>
          </div>

          <div className="crystalball-stats-panel">
            <h3 className="crystalball-stats-title">🎲 REALM STATS</h3>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">Total Games</span>
              <span className="crystalball-stats-value">{gameStats.totalGames}</span>
            </div>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">Total Bets</span>
              <span className="crystalball-stats-value">{formatNumber(gameStats.totalBets)} BLB</span>
            </div>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">Total Payouts</span>
              <span className="crystalball-stats-value">{formatNumber(gameStats.totalPayouts)} BLB</span>
            </div>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">House Edge</span>
              <span className="crystalball-stats-value">{gameStats.houseEdge}%</span>
            </div>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">Mystics Online</span>
              <span className="crystalball-stats-value">{gameStats.playersOnline}</span>
            </div>
          </div>

          <div className="crystalball-stats-panel">
            <h3 className="crystalball-stats-title">🔮 ORB WISDOM</h3>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">Most Powerful</span>
              <span className="crystalball-stats-value">Crystal Orb</span>
            </div>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">House Chance</span>
              <span className="crystalball-stats-value">{adminData.gameConfig ? (adminData.gameConfig.noWinChance / 100).toFixed(1) + '%' : '50%'}</span>
            </div>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">Best Payout</span>
              <span className="crystalball-stats-value">{getMultiplier()}</span>
            </div>
            <div className="crystalball-stats-item">
              <span className="crystalball-stats-label">Strategy</span>
              <span className="crystalball-stats-value">Trust the Crystal</span>
            </div>
          </div>

          {gameHistory.length > 0 && (
            <div className="crystalball-stats-panel">
              <h3 className="crystalball-stats-title">📜 MYSTICAL HISTORY</h3>
              <div className="crystalball-history-container">
                {gameHistory.map((vision, index) => (
                  <div key={index} className="crystalball-history-item">
                    <div className="crystalball-history-orbs">
                      <div className="crystalball-history-orb">{orbSymbols[vision.playerChoice]}</div>
                      <div className="crystalball-history-arrow">→</div>
                      <div className="crystalball-history-orb">
                        {vision.result < 5 ? orbSymbols[vision.result] : '💀'}
                      </div>
                    </div>
                    <div className="crystalball-history-result">
                      <div className="crystalball-history-details">
                        <div className="crystalball-history-bet">
                          Chose {orbNames[vision.playerChoice]} • {formatNumber(vision.betAmount)} BLB
                        </div>
                        <div className="crystalball-history-outcome">
                          Result: {vision.result < 5 ? orbNames[vision.result] : 'House'}
                        </div>
                      </div>
                      <div className={`crystalball-history-payout ${
                        vision.won ? 'crystalball-history-win' : 'crystalball-history-loss'
                      }`}>
                        {vision.won ? `+${formatNumber(vision.netPayout)}` : 'DEFEAT'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrystalBallFortune;